import projectionConfig from '@/data/serviceMapProjection.json';
import { getServiceMapLabelPreset } from '@/data/serviceMapMarkerMeta';

const { width, height, padding, bounds } = projectionConfig;
const innerWidth = width - padding.x * 2;
const innerHeight = height - padding.y * 2;

const clamp = (value, min, max) => {
  return Math.min(max, Math.max(min, value));
};

const toRadians = (value) => {
  return (value * Math.PI) / 180;
};

const clampLatitude = (value) => {
  return clamp(value, -85, 85);
};

const mercatorY = (latitude) => {
  const lat = clampLatitude(latitude);
  return Math.log(Math.tan(Math.PI / 4 + toRadians(lat) / 2));
};

const mercatorMinY = mercatorY(bounds.minLat);
const mercatorMaxY = mercatorY(bounds.maxLat);

const anchorTransforms = {
  start: 'translate(0, -50%)',
  center: 'translate(-50%, -50%)',
  end: 'translate(-100%, -50%)'
};

export const serviceMapAspectRatio = `${width} / ${height}`;
export const serviceMapDimensions = projectionConfig;

export const projectServiceMapCoordinates = (coordinates) => {
  if (!coordinates || typeof coordinates.lng !== 'number' || typeof coordinates.lat !== 'number') {
    return null;
  }

  const x = padding.x + ((coordinates.lng - bounds.minLon) / (bounds.maxLon - bounds.minLon)) * innerWidth;
  const y = padding.y + ((mercatorMaxY - mercatorY(coordinates.lat)) / (mercatorMaxY - mercatorMinY)) * innerHeight;

  const clampedX = clamp(x, 0, width);
  const clampedY = clamp(y, 0, height);

  return {
    x: clampedX,
    y: clampedY,
    xPercent: (clampedX / width) * 100,
    yPercent: (clampedY / height) * 100
  };
};

export const createServiceMapMarker = ({
  id,
  label = '',
  coordinates,
  presetId = id,
  labelPreset = {}
}) => {
  const projected = projectServiceMapCoordinates(coordinates);

  if (!projected) {
    return null;
  }

  const preset = {
    markerDx: 0,
    markerDy: 0,
    dx: 16,
    dy: -14,
    anchor: 'start',
    ...(getServiceMapLabelPreset(presetId) || {}),
    ...labelPreset
  };

  const markerX = projected.x;
  const markerY = projected.y;
  const labelX = clamp(markerX + preset.dx + preset.markerDx, 56, width - 56);
  const labelY = clamp(markerY + preset.dy + preset.markerDy, 44, height - 44);

  return {
    id,
    label,
    x: (markerX / width) * 100,
    y: (markerY / height) * 100,
    xPx: markerX,
    yPx: markerY,
    anchorX: projected.xPercent,
    anchorY: projected.yPercent,
    anchorXPx: projected.x,
    anchorYPx: projected.y,
    hasConnector: false,
    labelX: (labelX / width) * 100,
    labelY: (labelY / height) * 100,
    labelXPx: labelX,
    labelYPx: labelY,
    labelTransform: anchorTransforms[preset.anchor] || anchorTransforms.start
  };
};
